import React from "react";
import PostCard from "./PostCard";
import DayBreak, { dayKey } from "./DayBreak";

// What an active Scrapbook looks like -- the content of the Sample tab, first in
// the row (Sam, 2026-09-11). The page opens on it while the wall has no real
// posts; it has no "Add a photo" box, and it stays until Sam says to pull it.
//
// These are the real PostCards, so the sample cannot drift from the real design.
// The whole block is `inert`: visible, but nothing in it can be clicked, focused
// or typed into, so no sample reaction or comment can ever reach the server.
// Every card also carries a SAMPLE tag, and the first real post replaces it all.
// The notice that says so is SampleNotice, the first thing on the Sample tab.
//
// The photographs are real, from the old family site (already on Cloudinary for
// The Family Footprint). Each caption was written to match what its picture
// actually shows. Sydney's and Blair's names are used with Sam's approval.

const CLOUD = "https://res.cloudinary.com/dlkqklcgb/image/upload/";

// No real member has this id, so the sample never shows a "remove" button.
const NOBODY = -1;

// Relative to today, so the sample always reads as a wall people are using.
function daysAgo(n, hour) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

const SAMPLES = [
  {
    author: "Blair",
    category: "adventures",
    caption: "Longwood yesterday. Worth the drive.",
    place: "Longwood Gardens",
    when_text: "Yesterday",
    photo: "v1788592723/footprint/2-longwood_gardens.jpg",
    ago: [0, 11],
    reactions: { "👏": 4, "❤️": 2 },
    comments: [["Sydney", "Going Sunday, thank you for the push"]],
  },
  {
    author: "Sydney",
    category: "memories",
    caption: "The Memorial Day flag crew, and one very good dog.",
    when_text: "May 2019",
    extra_note: "Flags for the family veterans",
    photo: "v1788592726/footprint/33239237_10156386522764694_919046722724823040_o.jpg",
    ago: [1, 14],
    reactions: { "❤️": 11, "🙏": 3 },
    comments: [["Blair", "We should do this again in the spring"]],
  },
  {
    author: "Blair",
    category: "memories",
    caption: "The flyer from that year, for anyone who wants to come next time.",
    place: "du Pont de Nemours Cemetery",
    when_text: "May 24, 2019",
    photo: "v1788592746/footprint/Memorial-Day-Flag-Event_2019_rev_sm.jpg",
    ago: [4, 9],
    reactions: { "🙏": 6 },
    comments: [],
  },
  {
    author: "Sydney",
    category: "life_updates",
    caption: "The Reflections 2000 banner. Does anyone still have the film?",
    photo: "v1788592787/footprint/reflections2000_featimg_2.jpg",
    ago: [9, 16],
    reactions: { "😮": 5 },
    comments: [["Blair", "I remember this! Asking around."]],
  },
];

// Shaped exactly like a post from GET /scrapbook/posts, so PostCard needs no
// special case for the sample.
const samplePosts = () =>
  SAMPLES.map((s, i) => {
    const created_at = daysAgo(...s.ago);
    return {
      id: `sample-${i}`,
      author: s.author,
      author_individual_id: NOBODY,
      category: s.category,
      caption: s.caption,
      place: s.place || null,
      when_text: s.when_text || null,
      extra_note: s.extra_note || null,
      attachments: [{ url: CLOUD + s.photo, resource_type: "image", original_filename: "Sample photograph" }],
      created_at,
      reactions: s.reactions,
      my_reactions: [],
      comment_count: s.comments.length,
      comments: s.comments.map(([author, body], j) => ({
        id: `sample-${i}-${j}`, author, body, author_individual_id: NOBODY, created_at,
      })),
    };
  });

// Sam's words, 2026-09-11 -- keep them as written. The first thing on the
// Sample tab.
// `tabs`: the Scrapbook's own tabs to point at ({ key, label }), Sample left out.
// `onPick(key)`: switches the Scrapbook to that tab, so each name is a real button.
export function SampleNotice({ tabs = [], onPick }) {
  return (
    <div className="max-w-[660px] mx-auto mb-[18px] text-center rounded-[12px]
                    border-2 border-dashed border-dupontGold/60 bg-white/70 dark:bg-neutral-800/70
                    px-[18px] py-[12px]">
      {/* Office feedback 2026-09-25: "sub tabs" wasn't a clear term, so name the
          actual tabs and point up at them. */}
      <div aria-hidden="true" className="text-dupontGold text-[22px] leading-none mb-[4px]">↑</div>
      <p className="font-body font-semibold text-[19px] leading-[1.4] text-dupontBlue dark:text-darkText">
        This is a sample page.
      </p>
      <p className="font-body text-[16px] leading-[1.5] text-dupontBlue dark:text-darkText mt-[4px]">
        To post, click one of the tabs above:
      </p>
      <div className="flex flex-wrap justify-center gap-[8px] mt-[8px]">
        {tabs.map((t) => (
          <button
            type="button"
            key={t.key}
            onClick={() => onPick(t.key)}
            className="font-body text-[14px] px-[10px] py-[2px] rounded-full border border-dupontGold/70 text-dupontBlue dark:text-darkText bg-white dark:bg-neutral-800
                       hover:bg-dupontGold/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-dupontGold"
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SampleScrapbook({ viewerId }) {
  const posts = samplePosts();
  return (
    <section aria-label="Sample Scrapbook page">
      <div inert="" className="pointer-events-none select-none">
        {posts.map((p, i) => (
          <React.Fragment key={p.id}>
            {(i === 0 || dayKey(p.created_at) !== dayKey(posts[i - 1].created_at)) && (
              <DayBreak when={p.created_at} />
            )}
            <div className="relative">
              <span aria-hidden="true"
                    className="absolute z-10 -top-[9px] left-1/2 -translate-x-1/2 px-[10px] py-[2px]
                               rounded-full bg-dupontGold text-white shadow-sm
                               font-blairBody font-semibold text-[12px] tracking-[0.18em] uppercase">
                Sample
              </span>
              <PostCard post={p} viewerId={viewerId} isAdmin={false} index={i} />
            </div>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

export default SampleScrapbook;
